import React, { useCallback, useRef, useState, useEffect } from 'react'
import { useEditor, EditorContent, ReactRenderer } from '@tiptap/react'
import { Extension } from '@tiptap/core'
import StarterKit from '@tiptap/starter-kit'
import Link from '@tiptap/extension-link'
import Mention from '@tiptap/extension-mention'
import tippy from 'tippy.js'
import { useMessageInputContext, useChannelStateContext } from 'stream-chat-react'
import EmojiPicker from 'emoji-picker-react'
import { useGetTeammember } from 'hooks/useGetTeammember'
import { LinkDialog } from './LinkDialog'
import { AppAvatar } from 'components/Common/AppAvatar/AppAvatar.component'
import { ReactComponent as FileUploadIcon } from 'assets/svgs/file-upload-icon.svg'
import { ReactComponent as MentionIcon } from 'assets/svgs/mention-icon.svg'
import { ReactComponent as HyperlinkIcon } from 'assets/svgs/hyperlink-icon.svg'
import { ReactComponent as BoldIcon } from 'assets/svgs/bold-icon.svg'
import { ReactComponent as ItalicIcon } from 'assets/svgs/italic-icon.svg'
import { ReactComponent as StrikethroughIcon } from 'assets/svgs/strikethrought-icon.svg'
import { ReactComponent as ListBulletIcon } from 'assets/svgs/list-bullet-icon.svg'
import { getFileIconFromMimeType } from 'utils/file-utils'
import { AppSendMessageIcon } from 'components/Common/AppSvgIcons/AppSendMessageIcon'

// Import Tiptap CSS
// import '@tiptap/core/style.css'

// Mention list component
interface MentionListProps {
  items: Array<{ id: string; label: string; email: string; firstName: string; lastName: string }>
  command: (item: { id: string; label: string; email: string; firstName: string; lastName: string }) => void
  selectedIndex: number
}

const MentionList = React.forwardRef<HTMLDivElement, MentionListProps>((props, ref) => {
  const { items, command, selectedIndex } = props

  const selectItem = useCallback((index: number) => {
    const item = items[index]
    if (item) {
      command(item)
    }
  }, [items, command])

  useEffect(() => {
    const navigationKeys = ['ArrowUp', 'ArrowDown', 'Enter']
    const onKeyDown = (e: KeyboardEvent) => {
      if (navigationKeys.includes(e.key)) {
        e.preventDefault()
        if (e.key === 'ArrowUp') {
          selectItem((selectedIndex + items.length - 1) % items.length)
        } else if (e.key === 'ArrowDown') {
          selectItem((selectedIndex + 1) % items.length)
        } else if (e.key === 'Enter') {
          selectItem(selectedIndex)
        }
        return true
      }
      return false
    }
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [selectedIndex, selectItem, items.length])

  return (
    <div 
      className="bg-white border border-gray-200 rounded-lg shadow-lg absolute bottom-full mb-2 left-0 right-0 z-50 min-w-[320px] max-h-[280px] overflow-y-auto"
      ref={ref}
    >
      {items.length ? (
        items.map((item, index) => {
          const initial = item.label
            ?.split(' ')
            .map((name: string) => name[0])
            .join('')
          
          return (
            <button
              key={item.id}
              className={`w-full px-4 py-3 cursor-pointer transition-colors duration-150 text-left ${
                index === selectedIndex ? 'bg-gray-50' : 'hover:bg-gray-50'
              }`}
              onClick={() => selectItem(index)}
            >
              <div className="flex items-center gap-3">
                <AppAvatar size="sm" type={'mixed'} shape="Rounded" text={initial} />
                <div className="flex flex-col min-w-0 flex-1">
                  <div className="text-sm font-medium text-gray-900 truncate">
                    {item.label}
                  </div>
                </div>
              </div>
            </button>
          )
        })
      ) : (
        <div className="px-4 py-3 text-sm text-gray-500">No result</div>
      )}
    </div>
  )
})

MentionList.displayName = 'MentionList'

interface RichTextInputProps {
  placeholder?: string
  onSubmit?: (content: string) => void
  onTextChange?: (text: string) => void
  initialContent?: string
  disabled?: boolean
  rightActions?: React.ReactNode
  onAttachClick?: () => void
}

export const RichTextInput: React.FC<RichTextInputProps> = ({
  placeholder = 'Type a message...',
  onSubmit,
  onTextChange,
  initialContent = '',
  disabled = false,
  rightActions,
  onAttachClick,
}) => {
  const messageInput = useMessageInputContext<any>()
  const { channel } = useChannelStateContext<any>()
  const teamMembers = useGetTeammember()
  const [showEmojiPicker, setShowEmojiPicker] = useState(false)
  const [showLinkDialog, setShowLinkDialog] = useState(false)
  const [linkDialogData, setLinkDialogData] = useState<{ text: string; url: string; isEditing: boolean }>({ text: '', url: '', isEditing: false })
  const emojiButtonRef = useRef<HTMLButtonElement>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [pendingUploads, setPendingUploads] = useState<Array<{
    id: string
    file: File
    status: 'queued' | 'uploading' | 'uploaded' | 'failed'
    url?: string
    isImage: boolean
  }>>([])


  // Helper function to format file size
  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
  }


  // Create mention suggestions from team members
  // Build mention suggestions if/when mention feature is re-enabled
  const mentionSuggestions = (teamMembers || []).map((member) => {
    const firstName = member.firstName?.toLowerCase() || 'user'
    const lastName = member.lastName?.toLowerCase() || ''
    const domain = 'company.com' // Default domain
    
    let email: string
    if (lastName) {
      email = `${firstName}.${lastName}@${domain}`
    } else {
      email = `${firstName}@${domain}`
    }
    
    return {
      id: member.id,
      label: `${member.firstName} ${member.lastName}`.trim(),
      email: email,
      firstName: member.firstName || '',
      lastName: member.lastName || '',
    }
  })

  const editor = useEditor({
    extensions: [
      // Custom keymap: Shift+Enter in lists creates a new list item (no hard break)
      Extension.create({
        name: 'customListKeymap',
        priority: 1000,
        addKeyboardShortcuts() {
          return {
            'Shift-Enter': () => {
              if (this.editor.isActive('bulletList') || this.editor.isActive('orderedList')) {
                return this.editor.chain().focus().splitListItem('listItem').run()
              }
              return false
            },
            // Swallow Enter so the editor doesn't create a new bullet item
            Enter: () => {
              return true
            },
          }
        },
      }),
      StarterKit.configure({
        heading: false,
        codeBlock: false,
        blockquote: false,
        hardBreak: false,
      }),
      Link.configure({
        openOnClick: true,
        autolink: true,
        defaultProtocol: 'https',
        HTMLAttributes: {
          class: 'text-app-primary-60 underline hover:text-app-primary-70 cursor-pointer',
        },
        validate: (href) => /^https?:\/\//.test(href),
      }),
      Mention.configure({
        HTMLAttributes: {
          class: 'mention bg-app-primary-10 rounded px-1 py-0.5 text-app-primary-70 font-medium',
        },
        renderText({ node }) {
          return `@${node.attrs.label || node.attrs.id}`
        },
        renderHTML({ node }) {
          return [
            'mention',
            {
              id: node.attrs.id,
            },
            `@${node.attrs.label}`,
          ]
        },
        suggestion: {
          items: ({ query }) => {
            const q = (query || '').toLowerCase()
            return mentionSuggestions
              .filter((item) => item.label.toLowerCase().includes(q))
              .slice(0, 5)
          },
          render: () => {
            let component: ReactRenderer | null = null
            let popup: any
            return {
              onStart: (props) => {
                component = new ReactRenderer(MentionList as any, {
                  props: {
                    ...props,
                    command: (item: any) => {
                      props.command({
                        id: item.id,
                        label: item.label,
                      } as any)
                    }
                  },
                  editor: props.editor,
                })
                popup = tippy('body', {
                  getReferenceClientRect: props.clientRect as any,
                  appendTo: () => document.body,
                  content: (component as any).element,
                  showOnCreate: true,
                  interactive: true,
                  trigger: 'manual',
                  placement: 'top-start',
                  offset: [0, 8],
                  theme: 'light',
                })
              },
              onUpdate(props) {
                component?.updateProps({
                  ...props,
                  command: (item: any) => {
                    props.command({
                      id: item.id,
                      label: item.label,
                    } as any)
                  }
                })
                popup?.[0]?.setProps({
                  getReferenceClientRect: props.clientRect,
                })
              },
              onKeyDown(props) {
                if (props.event.key === 'Escape') {
                  popup?.[0]?.hide()
                  return true
                }
                return (component as any)?.ref?.onKeyDown?.(props.event) ?? false
              },
              onExit() {
                if (popup && popup[0] && !popup[0].state.isDestroyed) {
                  popup[0].destroy()
                }
                if (component) {
                  component.destroy()
                }
              },
            }
          },
        },
      }),
    ],
    content: initialContent || '',
    editable: !disabled,
    onUpdate: ({ editor }) => {
      const text = editor.getText()
      onTextChange?.(text)
      if (messageInput?.setText) {
        messageInput.setText(text)
      }
    },
    onSelectionUpdate: ({ editor }) => {
      // Auto-deactivate link formatting when cursor moves away from links
      if (!editor.isActive('link')) {
        // This helps prevent new text from being included in links
      }
    },
    editorProps: {
      attributes: {
        class: 'prose prose-sm max-w-none focus:outline-none min-h-[70px] px-3 py-2',
        'data-placeholder': placeholder,
      } as any,
    },
  })

  // Handle emoji insertion
  const handleEmojiClick = useCallback((emojiData: any) => {
    if (editor) {
      editor.chain().focus().insertContent(emojiData.emoji).run()
      setShowEmojiPicker(false)
    }
  }, [editor])

  // Handle link dialog
  const handleLinkClick = useCallback(() => {
    if (!editor) return

    // Get selected text or use empty string
    const selectedText = editor.state.doc.textBetween(
      editor.state.selection.from,
      editor.state.selection.to
    )
    
    // Check if there's already a link at the current position
    const { from, to } = editor.state.selection
    const linkMark = editor.state.doc.rangeHasMark(from, to, editor.schema.marks.link)
    
    if (linkMark) {
      // If there's a link, get its attributes for editing
      const linkAttrs = editor.getAttributes('link')
      setLinkDialogData({
        text: selectedText || linkAttrs.href || '',
        url: linkAttrs.href || '',
        isEditing: true
      })
    } else {
      setLinkDialogData({
        text: selectedText,
        url: '',
        isEditing: false
      })
    }
    
    setShowLinkDialog(true)
  }, [editor])

  const handleLinkSave = useCallback((text: string, url: string) => {
    if (!editor || !url) return

    // If there's selected text, apply link to the selection
    if (editor.state.selection.from !== editor.state.selection.to) {
      editor.chain().focus().setLink({ href: url }).run()
    } else {
      // If no text selected, insert the link text and apply link
      const linkText = text || url
      
      // Use a more reliable approach: insert content with link in one operation
      editor.chain().focus().insertContent(`<a href="${url}">${linkText}</a>`).run()
    }
    
    // After inserting or updating the link, move cursor to the end
    // but don't immediately unset the link formatting to avoid removing the link
    setTimeout(() => {
      if (editor) {
        const { to } = editor.state.selection
        // Just move cursor to the end, don't unset link formatting yet
        editor.chain().focus().setTextSelection(to).run()
      }
    }, 0)
  }, [editor])

  const handleLinkRemove = useCallback(() => {
    if (!editor) return
    editor.chain().focus().unsetLink().run()
  }, [editor])

  // File handling: managed locally in rich text input
  const triggerFilePicker = useCallback(() => {
    if (onAttachClick) onAttachClick()
    fileInputRef.current?.click()
  }, [onAttachClick])

  const handleFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files
    if (!files || files.length === 0) return
    const items: Array<{ id: string; file: File; status: 'queued'; url?: string; isImage: boolean }> = []
    Array.from(files).forEach((file) => {
      items.push({ id: `${Date.now()}-${file.name}-${Math.random()}`, file, status: 'queued', isImage: file.type?.startsWith('image/') })
    })
    setPendingUploads((prev) => [...prev, ...items])
    e.target.value = ''
  }, [])

  // Handle form submission
  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault()
    if (!editor) return

    const text = editor.getText()
    const html = editor.getHTML()
    
    // Custom HTML serializer to ensure mentions are properly formatted
    let customHtml = ''
    let hasMentions = false
    
    // Helper function to serialize text with marks
    const serializeTextWithMarks = (textNode: any): string => {
      let textContent = textNode.text
      if (textNode.marks) {
        textNode.marks.forEach((mark: any) => {
          if (mark.type?.name === 'bold') {
            textContent = `<strong>${textContent}</strong>`
          } else if (mark.type?.name === 'italic') {
            textContent = `<em>${textContent}</em>`
          } else if (mark.type?.name === 'strike') {
            textContent = `<s>${textContent}</s>`
          } else if (mark.type?.name === 'link') {
            textContent = `<a href="${mark.attrs.href}">${textContent}</a>`
          }
        })
      }
      return textContent
    }
    
    // Helper function to serialize paragraph content
    const serializeParagraphContent = (paragraph: any): string => {
      let paragraphContent = ''
      paragraph.content.forEach((child: any) => {
        if (child.type?.name === 'text') {
          paragraphContent += serializeTextWithMarks(child)
        } else if (child.type?.name === 'mention') {
          paragraphContent += `<mention id="${child.attrs.id}">@${child.attrs.label}</mention>`
          hasMentions = true
        }
      })
      return paragraphContent
    }
    
    // Process only top-level nodes to avoid duplication
    editor.state.doc.content.forEach((node: any) => {
      if (node.type?.name === 'paragraph') {
        const paragraphContent = serializeParagraphContent(node)
        customHtml += `<p>${paragraphContent}</p>`
      } else if (node.type?.name === 'bulletList') {
        customHtml += '<ul>'
        node.content.forEach((listItem: any) => {
          if (listItem.type?.name === 'listItem') {
            customHtml += '<li>'
            listItem.content.forEach((paragraph: any) => {
              if (paragraph.type?.name === 'paragraph') {
                customHtml += serializeParagraphContent(paragraph)
              }
            })
            customHtml += '</li>'
          }
        })
        customHtml += '</ul>'
      } else if (node.type?.name === 'orderedList') {
        customHtml += '<ol>'
        node.content.forEach((listItem: any) => {
          if (listItem.type?.name === 'listItem') {
            customHtml += '<li>'
            listItem.content.forEach((paragraph: any) => {
              if (paragraph.type?.name === 'paragraph') {
                customHtml += serializeParagraphContent(paragraph)
              }
            })
            customHtml += '</li>'
          }
        })
        customHtml += '</ol>'
      }
    })
    
    // Use custom HTML if we have mentions, otherwise use regular HTML
    const finalHtml = hasMentions ? customHtml.trim() : html
 

    // Check for uploads in both systems
    const hasPendingUploads = pendingUploads.length > 0
    const hasStreamUploads = (messageInput.fileUploads && Object.keys(messageInput.fileUploads).length > 0) ||
                            (messageInput.imageUploads && Object.keys(messageInput.imageUploads).length > 0)
    const hasUploads = hasPendingUploads || hasStreamUploads
    
    // If nothing to send and no uploads queued, bail
    if (!text.trim() && !hasUploads) return

    try {
      // Upload pending files to Stream CDN and build attachments
      const attachments: any[] = []
      if (channel && pendingUploads.length > 0) {
        // Mark uploading
        setPendingUploads((prev) => prev.map((u) => ({ ...u, status: 'uploading' })))
        for (const item of pendingUploads) {
          try {
            let uploadResult: any
            if (item.isImage && (channel as any).sendImage) {
              uploadResult = await (channel as any).sendImage(item.file)
            } else if ((channel as any).sendFile) {
              uploadResult = await (channel as any).sendFile(item.file)
            } else {
              throw new Error('Channel file upload API not available')
            }
            const url: string = uploadResult?.file || uploadResult?.thumb_url || uploadResult?.url
            attachments.push(
              item.isImage
                ? {
                    type: 'image',
                    image_url: url,
                    file_name: item.file.name,
                    mime_type: item.file.type,
                    file_size: item.file.size,
                  }
                : {
                    type: 'file',
                    asset_url: url,
                    file_name: item.file.name,
                    mime_type: item.file.type,
                    file_size: item.file.size,
                  }
            )
            setPendingUploads((prev) => prev.map((u) => (u.id === item.id ? { ...u, status: 'uploaded', url } : u)))
          } catch (err) {
            console.error('Upload failed for file:', item.file.name, err)
            setPendingUploads((prev) => prev.map((u) => (u.id === item.id ? { ...u, status: 'failed' } : u)))
          }
        }
      }

      // Add Stream Chat file uploads to attachments
      if (messageInput.fileUploads) {
        Object.values(messageInput.fileUploads).forEach((upload) => {
          if (upload.state === 'finished' && upload.file) {
            attachments.push({
              type: 'file',
              file: upload.file,
              file_name: upload.file.name,
              mime_type: upload.file.type,
              file_size: upload.file.size,
              asset_url: upload.url
            })
          }
        })
      }

      // Add Stream Chat image uploads to attachments
      if (messageInput.imageUploads) {
        Object.values(messageInput.imageUploads).forEach((upload) => {
          if (upload.state === 'finished' && upload.file) {
            attachments.push({
              type: 'image',
              file: upload.file,
              file_name: upload.file.name,
              mime_type: upload.file.type,
              file_size: upload.file.size,
              image_url: upload.url
            })
          }
        })
      }

      // Collect mentioned users from document in the same format as MentionTextArea
      const mentionedUserIds: string[] = []
      const mentions: any[] = []
      editor.state.doc.descendants((node: any) => {
        if (node.type?.name === 'mention' && node.attrs?.id) {
          const mentionData = mentionSuggestions.find(s => s.id === node.attrs.id)
          mentionedUserIds.push(String(node.attrs.id))
          mentions.push({
            id: node.attrs.id,
            display: node.attrs.label,
            email: mentionData?.email || '',
            firstName: mentionData?.firstName || '',
            lastName: mentionData?.lastName || '',
          })
        }
      })

  
      if (channel) {
        // Check if there are voice recordings and get their metadata
        const customData: any = { 
          richText: true, 
          richTextHtml: finalHtml, // Use final HTML with proper mention tags
          mentions: mentions // Include mentions in the same format as MentionTextArea
        }
        
        // Look for voice recordings in the file uploads
        if (messageInput.fileUploads) {
          const voiceFiles = Object.values(messageInput.fileUploads).filter(upload => 
            upload.file?.type?.startsWith('audio/') && upload.state === 'finished'
          )
          
          if (voiceFiles.length > 0) {
            // For now, we'll add a flag to indicate voice messages
            // The actual duration would need to be passed from the parent component
            customData.hasVoiceMessage = true
            customData.voiceFileCount = voiceFiles.length
          }
        }
        
 
        console.log({
          hasMentions,
          finalHtml,
          text,
          messageInput
        })
        
        await channel.sendMessage({
          text: hasMentions ? finalHtml : text,
          html: finalHtml,
          attachments,
          // Ensure replies are properly threaded when replying from thread context
          ...(messageInput?.parent?.id ? { parent_id: messageInput.parent.id, show_in_channel: false } : {}),
          custom: customData,
        } as any)

        // Clear editor and Stream input state
        editor.commands.clearContent()
        messageInput?.setText?.('')
        setPendingUploads([])
        
        // Clear Stream Chat file uploads
        if (messageInput.fileUploads) {
          Object.keys(messageInput.fileUploads).forEach((uploadId) => {
            if (messageInput.removeFile) {
              messageInput.removeFile(uploadId)
            }
          })
        }
        
        if (messageInput.imageUploads) {
          Object.keys(messageInput.imageUploads).forEach((uploadId) => {
            if (messageInput.removeImage) {
              messageInput.removeImage(uploadId)
            }
          })
        }
        
        return
      }

      // Fallback
      onSubmit?.(finalHtml)
      editor.commands.clearContent()
    } catch (err) {
      // Fallback
      onSubmit?.(finalHtml)
      editor.commands.clearContent()
    }
  }, [editor, channel, messageInput, onSubmit, pendingUploads, mentionSuggestions])

  // Handle keyboard shortcuts
  useEffect(() => {
    if (!editor) return

    const handleKeyDown = (event: KeyboardEvent) => {
      // Handle Enter key
      if (event.key === 'Enter' && !event.shiftKey) {
        event.preventDefault()
        event.stopPropagation()
        handleSubmit(event as any)
      }
      
      // Handle Ctrl/Cmd + B for bold
      if ((event.ctrlKey || event.metaKey) && event.key === 'b') {
        event.preventDefault()
        editor.chain().focus().toggleBold().run()
      }
      
      // Handle Ctrl/Cmd + I for italic
      if ((event.ctrlKey || event.metaKey) && event.key === 'i') {
        event.preventDefault()
        editor.chain().focus().toggleItalic().run()
      }
      
      // Handle Ctrl/Cmd + K for link
      if ((event.ctrlKey || event.metaKey) && event.key === 'k') {
        event.preventDefault()
        handleLinkClick()
      }
      
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [editor, handleSubmit, handleLinkClick])

  if (!editor) {
    return null
  }

  return (
    <div className="rich-text-input-container">
      <form onSubmit={handleSubmit} className="flex flex-col">
        {/* Input container matching design */}
        <div className="border border-app-primary-30 rounded-xl bg-white focus-within:border-app-primary-60 transition-colors">
          {/* Editor */}
          {editor ? <EditorContent editor={editor} /> : null}

          {/* Toolbar inside container bottom */}
          <div className="flex items-center justify-between px-2 py-2">
            <div className="flex items-center gap-1">
              {/* Attach */}
              <button
                type="button"
                onClick={triggerFilePicker}
                className="p-2 rounded hover:bg-app-grey-20 transition-colors"
                title="Attach file"
              >
                <FileUploadIcon />
              </button>
              {/* Hidden file input for attachments */}
              <input
                ref={fileInputRef}
                type="file"
                multiple
                onChange={handleFileChange}
                style={{ display: 'none' }}
              />

              {/* Emoji */}
              <div className="relative">
                <button
                  ref={emojiButtonRef}
                  type="button"
                  onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                  className="p-2 rounded hover:bg-app-grey-20 transition-colors"
                  title="Insert Emoji"
                >
                  <span className="text-sm">🙂</span>
                </button>
                {showEmojiPicker && (
                  <div className="absolute bottom-full mb-2 left-0 z-50">
                    <EmojiPicker onEmojiClick={(emojiData) => handleEmojiClick(emojiData)} />
                  </div>
                )}
              </div>

              {/* Mention */}
              <button
                type="button"
                onClick={() => editor.chain().focus().insertContent('@').run()}
                className="p-2 rounded hover:bg-app-grey-20 transition-colors"
                title="Mention User"
              >
                <MentionIcon />
              </button>

              {/* Link */}
              <button
                type="button"
                onClick={handleLinkClick}
                className={`p-2 rounded hover:bg-app-grey-20 transition-colors ${
                  editor.isActive('link') ? 'bg-app-grey-20' : ''
                }`}
                title={editor.isActive('link') ? 'Edit Link' : 'Insert Link (Ctrl+K)'}
              >
                <HyperlinkIcon />
              </button>

              {/* Divider */}
              <div className="w-px h-6 bg-app-grey-25 mx-1" />

          {/* Bold */}
          <button
            type="button"
            onClick={() => editor.chain().focus().toggleBold().run()}
            className={`p-2 rounded hover:bg-app-grey-20 transition-colors ${
              editor.isActive('bold') ? 'bg-app-grey-20' : ''
            }`}
            title="Bold (Ctrl+B)"
          >
            <BoldIcon />
          </button>

          {/* Italic */}
          <button
            type="button"
            onClick={() => editor.chain().focus().toggleItalic().run()}
            className={`p-2 rounded hover:bg-app-grey-20 transition-colors ${
              editor.isActive('italic') ? 'bg-app-grey-20' : ''
            }`}
            title="Italic (Ctrl+I)"
          >
            <ItalicIcon />
          </button>

          {/* Strikethrough */}
          <button
            type="button"
            onClick={() => editor.chain().focus().toggleStrike().run()}
            className={`p-2 rounded hover:bg-app-grey-20 transition-colors ${
              editor.isActive('strike') ? 'bg-app-grey-20' : ''
            }`}
            title="Strikethrough"
          >
            <StrikethroughIcon />
          </button>
          <div className="w-px h-6 bg-app-grey-25 mx-1" />
          {/* Bullet List */}
          <button
            type="button"
            onClick={() => editor.chain().focus().toggleBulletList().run()}
            className={`p-2 rounded hover:bg-app-grey-20 transition-colors ${
              editor.isActive('bulletList') ? 'bg-app-grey-20' : ''
            }`}
            title="Bullet List"
          >
            <ListBulletIcon />
          </button>

            </div>

            <div className="flex items-center gap-2">
              {rightActions}
              <button
                type="submit"
                disabled={editor.isEmpty || disabled}
                className="w-9 h-9 rounded-2xl bg-app-primary-60 text-white flex items-center justify-center hover:bg-app-primary-70 disabled:bg-app-primary-20 disabled:text-app-primary-60 disabled:cursor-not-allowed transition-colors shadow-sm"
                title="Send"
              >
                <AppSendMessageIcon />
              </button>
            </div>
          </div>
        </div>
      </form>

      {/* Pending uploads preview */}
      {pendingUploads.length > 0 && (
        <div className="mt-2 space-y-2">
          {pendingUploads.map((u) => (
            <div key={u.id} className="flex items-center gap-3 p-2 bg-app-grey-5 rounded border border-app-grey-25">
              <div className="w-8 h-8 flex items-center justify-center">
                {u.isImage ? '🖼️' : '📎'}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm truncate text-app-grey-80">{u.file.name}</p>
                <p className="text-xs text-app-grey-60">
                  {u.status === 'queued' && 'Queued'}
                  {u.status === 'uploading' && 'Uploading...'}
                  {u.status === 'uploaded' && 'Ready'}
                  {u.status === 'failed' && 'Failed'}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setPendingUploads((prev) => prev.filter((x) => x.id !== u.id))}
                className="p-1 text-app-grey-60 hover:text-red-500"
              >
                ✕
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Stream Chat File Uploads Preview */}
      {((messageInput.fileUploads && Object.keys(messageInput.fileUploads).length > 0) ||
        (messageInput.imageUploads && Object.keys(messageInput.imageUploads).length > 0)) && (
        <div className="mt-2 space-y-2">
          {/* Image Uploads */}
          {Object.values(messageInput.imageUploads || {}).map((upload, index) => (
            <div
              key={`stream-image-${upload.id || index}`}
              className="flex items-center gap-3 p-3 bg-app-grey-5 rounded-lg border border-app-grey-25"
            >
              {/* Image Preview */}
              <div className="flex-shrink-0 relative">
                <div className="w-12 h-12 rounded-lg overflow-hidden bg-app-grey-20 border border-app-grey-30">
                  {upload.url ? (
                    <>
                      <img
                        src={upload.url}
                        alt={upload.file?.name || 'Image preview'}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          const target = e.target as HTMLImageElement
                          target.style.display = 'none'
                          const fallback = target.nextElementSibling as HTMLElement
                          if (fallback) fallback.style.display = 'flex'
                        }}
                      />
                      <div className="w-full h-full hidden items-center justify-center text-app-grey-60">
                        🖼️
                      </div>
                    </>
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-app-grey-60">
                      🖼️
                    </div>
                  )}
                </div>

                {/* Upload Status Overlay */}
                {upload.state === 'uploading' && (
                  <div className="absolute inset-0 bg-black bg-opacity-50 rounded-lg flex items-center justify-center">
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  </div>
                )}
                {upload.state === 'finished' && (
                  <div className="absolute -top-1 -right-1 w-5 h-5 bg-green-500 rounded-full flex items-center justify-center">
                    <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                )}
                {upload.state === 'failed' && (
                  <div className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center">
                    <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </div>
                )}
              </div>

              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-app-grey-80 truncate">
                      {upload.file?.name || 'Image'}
                    </p>
                    <div className="flex items-center gap-2 text-xs text-app-grey-60">
                      {upload.file?.size && <span>{formatFileSize(upload.file.size)}</span>}
                      {upload.state === 'uploading' && <span className="text-app-primary-60">Uploading...</span>}
                      {upload.state === 'finished' && <span className="text-green-600">Uploaded</span>}
                      {upload.state === 'failed' && <span className="text-red-600">Failed</span>}
                    </div>
                  </div>
                </div>
              </div>

              {upload.state !== 'uploading' && (
                <button
                  type="button"
                  onClick={() =>
                    messageInput.removeImage &&
                    messageInput.removeImage(upload.id || index.toString())
                  }
                  className="flex-shrink-0 p-1 text-app-grey-60 hover:text-red-500 transition-colors"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
            </div>
          ))}

          {/* File Uploads */}
          {Object.values(messageInput.fileUploads || {}).map((upload, index) => (
            <div
              key={`stream-file-${upload.id || index}`}
              className="flex items-center gap-3 p-3 bg-app-grey-5 rounded-lg border border-app-grey-25"
            >
              <div className="flex-shrink-0">
                {upload.state === 'uploading' ? (
                  <div className="w-8 h-8 flex items-center justify-center">
                    <div className="w-5 h-5 border-2 border-app-primary-60 border-t-transparent rounded-full animate-spin"></div>
                  </div>
                ) : upload.state === 'finished' ? (
                  <div className="w-8 h-8 flex items-center justify-center text-green-500">✓</div>
                ) : (
                  <div className="w-8 h-8 flex items-center justify-center text-red-500">✗</div>
                )}
              </div>

              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-lg">{getFileIconFromMimeType(upload.file?.type || '')}</span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-app-grey-80 truncate">
                      {upload.file?.name || 'File'}
                    </p>
                    <div className="flex items-center gap-2 text-xs text-app-grey-60">
                      {upload.file?.size && <span>{formatFileSize(upload.file.size)}</span>}
                      {upload.state === 'uploading' && <span className="text-app-primary-60">Uploading...</span>}
                      {upload.state === 'finished' && <span className="text-green-600">Uploaded</span>}
                      {upload.state === 'failed' && <span className="text-red-600">Failed</span>}
                    </div>
                  </div>
                </div>
              </div>

              {upload.state !== 'uploading' && (
                <button
                  type="button"
                  onClick={() =>
                    messageInput.removeFile &&
                    messageInput.removeFile(upload.id || index.toString())
                  }
                  className="flex-shrink-0 p-1 text-app-grey-60 hover:text-red-500 transition-colors"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Link Dialog */}
      <LinkDialog
        isOpen={showLinkDialog}
        onClose={() => setShowLinkDialog(false)}
        onSave={handleLinkSave}
        onRemove={handleLinkRemove}
        initialText={linkDialogData.text}
        initialUrl={linkDialogData.url}
        isEditing={linkDialogData.isEditing}
      />

      {/* Styles */}
      <style>{`
        .rich-text-input-container .ProseMirror {
          outline: none;
          min-height: 56px;
          padding: 8px 12px;
          border: none;
        }
        .rich-text-input-container .ProseMirror p.is-editor-empty:first-child::before {
          color: var(--color-app-grey-60, #7C7C7C);
          content: attr(data-placeholder);
          float: left;
          height: 0;
          pointer-events: none;
        }
      `}</style>
    </div>
  )
}
